import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, KPI, Button, Empty } from "@/components/Primitives";
import { StatusPill, invoiceStatusVariant } from "@/components/StatusPill";
import { ArrowLeft, Download, CreditCard } from "lucide-react";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

function woStatusVariant(s) {
  if (s === "completed") return "success";
  if (s === "in_progress") return "primary";
  if (s === "cancelled") return "danger";
  if (s === "submitted") return "warning";
  return "default";
}

export default function CustomerProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setData(data);
      } catch (e) { console.error(e); }
    })();
  }, [id]);

  if (!data) return <div className="text-sm text-muted">Loading…</div>;
  const { project: p, financials: f, invoices, work_orders } = data;
  const isFixed = (f.billing_type || "fixed") === "fixed" && (f.budget || 0) > 0;

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/my-projects")} className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6}/>Back to projects
      </button>

      <div className="relative rounded-2xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <div className="relative h-52">
          {p.cover_image_url
            ? <img src={p.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))]" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--surface))] via-[hsl(var(--surface))]/40 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">{p.project_type} · {p.customer_name}</div>
              <h1 className="text-[30px] font-bold tracking-tight leading-tight mt-1">{p.name}</h1>
            </div>
            <StatusPill variant={p.status === "completed" ? "success" : p.status === "in_progress" ? "primary" : "default"}>{p.status.replace("_", " ")}</StatusPill>
          </div>
        </div>

        <div className="p-6 border-t border-[hsl(var(--border))]">
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] mb-2 font-semibold">About</div>
          <p className="text-[14px] leading-relaxed">{p.description || "No description yet."}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        {isFixed
          ? <KPI label="Invoiced / Cost" value={`${gbp(f.invoiced)} / ${gbp(f.cost)}`} />
          : <KPI label="Total invoiced" value={gbp(f.invoiced)} />}
        <KPI label="Paid" value={gbp(f.paid)} variant="success" />
        <KPI label="Outstanding" value={gbp(f.outstanding)} variant={f.outstanding > 0 ? "warning" : "default"} />
        <KPI label="# Invoices" value={f.invoice_count} />
      </div>

      {isFixed && (
        <Card title="Payment progress" right={<span className="text-[12px] font-mono text-[hsl(var(--text-muted))]">{f.payment_progress}%</span>}>
          <div className="p-5">
            <div className="h-3 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[hsl(var(--success))] to-[hsl(var(--primary))] rounded-full transition-all" style={{ width: `${f.payment_progress}%` }} />
            </div>
            <div className="mt-3 flex justify-between text-[11px] text-[hsl(var(--text-muted))]">
              <span>£0</span>
              <span>{gbp(f.cost)}</span>
            </div>
          </div>
        </Card>
      )}

      <Card title="Latest invoices" right={<button className="text-[12px] text-[hsl(var(--primary))] font-semibold" onClick={() => nav("/my-invoices")}>View all</button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))]">
                <th className="text-left font-semibold px-5 py-3">Invoice</th>
                <th className="text-left font-semibold px-5 py-3">Issued</th>
                <th className="text-right font-semibold px-5 py-3">Amount</th>
                <th className="text-left font-semibold px-5 py-3 pl-8">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-hover))]">
                  <td className="px-5 py-3 font-mono font-semibold">{i.number}</td>
                  <td className="px-5 py-3 text-[hsl(var(--text-muted))]">{new Date(i.issued_date).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right font-mono font-semibold">{gbp(i.amount)}</td>
                  <td className="px-5 py-3 pl-8"><StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill></td>
                  <td className="px-5 py-3 text-right">
                    {i.status === "overdue" || i.status === "pending"
                      ? <Button size="sm"><CreditCard className="w-3.5 h-3.5" strokeWidth={1.6}/>Pay now</Button>
                      : <Button size="sm" variant="ghost"><Download className="w-3.5 h-3.5" strokeWidth={1.6}/>PDF</Button>}
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={5}><Empty title="No invoices for this project yet"/></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Work orders on this project">
        <div className="divide-y divide-[hsl(var(--border))]">
          {work_orders.map((w) => (
            <div key={w.id} className="px-5 py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold truncate">{w.title}</div>
                <div className="text-[11px] text-[hsl(var(--text-muted))]">{w.priority} · {w.assignee || "Unassigned"} · {new Date(w.created_at).toLocaleDateString()}</div>
              </div>
              <StatusPill variant={woStatusVariant(w.status)}>{w.status.replace("_", " ")}</StatusPill>
            </div>
          ))}
          {work_orders.length === 0 && <Empty title="No work orders yet" subtitle="Submit one from your dashboard to get started."/>}
        </div>
      </Card>
    </div>
  );
}
